import { useState, useEffect, useCallback, useRef } from 'react';

// Define types for notifications
interface NotificationData {
  title: string;
  content?: string;
}

export interface Notification {
  id: string; // Add unique ID for each notification
  notification_type: "INTERVIEW_SUCCESS" | "GENERATED_INTERVIEW_INSIGHT" | "CUSTOM";
  timestamp: string;
  data: NotificationData;
  read: boolean; // Track if notification has been read
}

interface UseNotificationsReturn {
  connected: boolean;
  notifications: Notification[];
  clearNotifications: () => void;
  dismissNotification: (id: string) => void; // Add method to dismiss individual notification
  markAllAsRead: () => void; // Add method to mark all as read
}

export const useNotifications = (userId: string, apiBaseUrl: string): UseNotificationsReturn => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processedNotificationsRef = useRef<Set<string>>(new Set()); // Track processed notifications to avoid duplicates

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Dismiss individual notification
  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  // Generate a unique ID for notifications
  const generateNotificationId = (message: any) => {
    return `${message.notification_type}_${message.timestamp}_${Math.random().toString(36).substring(2, 9)}`;
  };

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!userId) {
      console.warn('User ID is required to establish WebSocket connection');
      return;
    }

    // Create WebSocket connection
    const wsUrl = `${apiBaseUrl.replace('http', 'ws')}/notifications/ws/${userId}`;
    console.log(`Attempting to connect to WebSocket at ${wsUrl}`);

    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('WebSocket connection established');
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'pong') {
          console.log('Received pong from server');
        } else if (message.event_type === 'connection_established') {
          console.log('Connection confirmed by server');
        } else {
            // must be a notification
            console.log(`Received notification: ${message.notification_type}`, message);
            
            // Create a fingerprint for deduplication
            const fingerprint = `${message.notification_type}_${JSON.stringify(message.data)}_${message.timestamp}`;

            // Only add if not a duplicate
            if (!processedNotificationsRef.current.has(fingerprint)) {
                processedNotificationsRef.current.add(fingerprint);
                
                // Add notification with unique ID and read state
                setNotifications(prev => [
                ...prev, 
                {
                    ...message,
                    id: generateNotificationId(message),
                    read: false
                }
                ]);
                
                // Automatically remove this fingerprint after some time to allow future similar notifications
                setTimeout(() => {
                processedNotificationsRef.current.delete(fingerprint);
                }, 60000); // Allow similar notifications after 1 minute
            }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnected(false);
    };

    ws.onclose = () => {
      console.log('WebSocket connection closed');
      setConnected(false);
      
      // Attempt to reconnect after a delay
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('Attempting to reconnect...');
        connect();
      }, 3000); // Reconnect after 3 seconds
    };

    setSocket(ws);
  }, [userId, apiBaseUrl]);

  // Setup WebSocket connection
  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (socket) {
        socket.close();
      }
    };
  }, [connect]);

  // Set up ping interval to keep connection alive
  useEffect(() => {
    if (!socket) return;
    
    const pingInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Send ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, [socket]);

  return {
    connected,
    notifications,
    clearNotifications,
    dismissNotification,
    markAllAsRead
  };
};